import axios from 'axios';
import { apiUrl } from '@algoan/dashboard/env';
import { loadConfigFailure, loadConfigStart, loadConfigSuccess } from '@algoan/dashboard/store/slice';
import { AsyncThunk } from '@algoan/dashboard/store/store';
import { authRequest } from './auth-thunks';
import {
  CreditInsightsConfig,
  DashboardConfig,
  defaultScoreConfig,
  TransactionDataConfig,
  VersionedConfig,
  ProjectConfig,
  ProjectProductsName,
} from '@algoan/dashboard/model';
import { isScoreConfigValid } from '@algoan/dashboard/calculators';
import { monitoring } from '@algoan/dashboard/utils';

const defaultProjectConfig: ProjectConfig = {
  id: '',
  name: 'Default Project',
  configuration: {
    aggregation: {
      enableRefresh: true,
    },
  },
  products: [
    { name: ProjectProductsName.DASHBOARD },
    { name: ProjectProductsName.CREDIT_INSIGHTS },
    { name: ProjectProductsName.BANK_DATA },
  ],
};

/**
 * @param organizationIds string containing multiple organization ids joined by a comma
 * @param projectIds string containing multiple project ids joined by a comma
 */
export const getConfigData =
  (organizationIds: string, projectIds: string): AsyncThunk<void> =>
  async (dispatch) => {
    dispatch(loadConfigStart());

    try {
      const organizationId = organizationIds.split(',');
      const projectId = projectIds.split(',');

      const projectConfig = await dispatch(fetchProjectConfig(organizationId, projectId));

      const hasDashboard = projectConfig?.products.some((product) => product.name === 'DASHBOARD');
      const hasCreditInsights = projectConfig?.products.some((product) => product.name === 'CREDIT_INSIGHTS');
      const hasBankData = projectConfig?.products.some((product) => product.name === 'BANK_DATA');

      const dashboardPromise = hasDashboard
        ? dispatch(fetchDashboardConfig(organizationId, projectId))
        : Promise.resolve(undefined);

      const creditInsightsPromise = hasCreditInsights
        ? dispatch(fetchCreditInsightsConfig(organizationId, projectId))
        : Promise.resolve(undefined);

      const bankDataPromise = hasBankData
        ? dispatch(fetchTransactionDataConfig(organizationId, projectId)).catch(() => undefined)
        : Promise.resolve(undefined);

      const [fetchedDashboardConfig, fetchedCreditInsightsConfig, fetchedTransactionDataConfig] = await Promise.all([
        dashboardPromise,
        creditInsightsPromise,
        bankDataPromise,
      ]);

      const getDashboardConfigToStore = (): DashboardConfig => {
        if (!fetchedDashboardConfig) {
          throw new Error('Dashboard configuration is undefined');
        }

        return isScoreConfigValid(fetchedDashboardConfig)
          ? fetchedDashboardConfig
          : { ...fetchedDashboardConfig, scoreConfig: defaultScoreConfig };
      };

      const dashboardConfig = getDashboardConfigToStore();
      dispatch(
        loadConfigSuccess({
          dashboard: dashboardConfig,
          creditInsights: fetchedCreditInsightsConfig,
          transactionDataConfig: fetchedTransactionDataConfig,
          projectConfig: projectConfig,
        }),
      );
    } catch {
      dispatch(loadConfigFailure());
    }
  };

export const fetchDashboardConfig =
  (organizationIds: string[], projectIds: string[]): AsyncThunk<DashboardConfig> =>
  async (dispatch) => {
    const getDefaultConfig = async (): Promise<DashboardConfig> => {
      const { data: config } = await axios.get<DashboardConfig>(`/config/default-config.json`);

      return config;
    };

    if (organizationIds[0] === '*' || projectIds[0] === '*') {
      return getDefaultConfig();
    }

    try {
      const { data: versionedConfig } = await dispatch(
        authRequest<VersionedConfig>(
          'get',
          // TODO: select project to fetch
          `${apiUrl}/v2/organizations/${organizationIds[0]}/projects/${projectIds[0]}/products/DASHBOARD/configurations`,
        ),
      );

      return versionedConfig;
    } catch {
      try {
        return (await axios.get(`/config/${projectIds.join('-')}.json`)).data;
      } catch {
        return getDefaultConfig();
      }
    }
  };

export const fetchCreditInsightsConfig =
  (organizationIds: string[], projectIds: string[]): AsyncThunk<CreditInsightsConfig | undefined> =>
  async (dispatch) => {
    if (organizationIds[0] === '*' || projectIds[0] === '*') {
      return undefined;
    }

    const { data: creditInsightsConfig } = await dispatch(
      authRequest<CreditInsightsConfig>(
        'get',
        // TODO: select project to fetch
        `${apiUrl}/v2/organizations/${organizationIds[0]}/projects/${projectIds[0]}/products/CREDIT_INSIGHTS/configurations`,
      ),
    );

    return creditInsightsConfig;
  };

export const fetchTransactionDataConfig =
  (organizationIds: string[], projectIds: string[]): AsyncThunk<TransactionDataConfig | undefined> =>
  async (dispatch) => {
    if (organizationIds[0] === '*' || projectIds[0] === '*') {
      return undefined;
    }

    const { data: transactionDataConfig } = await dispatch(
      authRequest<TransactionDataConfig>(
        'get',
        `${apiUrl}/v2/organizations/${organizationIds[0]}/projects/${projectIds[0]}/products/BANK_DATA/configurations`,
      ),
    );

    return transactionDataConfig;
  };

export const fetchProjectConfig =
  (organizationIds: string[], projectIds: string[]): AsyncThunk<ProjectConfig | undefined> =>
  async (dispatch) => {
    if (organizationIds[0] === '*' || projectIds[0] === '*') {
      return defaultProjectConfig;
    }

    const { data: projectConfig } = await dispatch(
      authRequest<ProjectConfig>('get', `${apiUrl}/v2/organizations/${organizationIds[0]}/projects/${projectIds[0]}`),
    );

    if (projectConfig.name) {
      monitoring.setGlobalContextProperty('projectName', projectConfig.name);
    }

    return projectConfig;
  };
